import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, useSearchParams } from 'react-router-dom';
import BetaMessage from '../components/BetaMessage/BetaMessage';
import IntegritasBtn from '../components/Login/IntegritasBtn';
import GoogleAuthErr from '../components/Login/GoogleAuthErr';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { INTEGRITAS_BASE_URL } from '../config/constants';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { login, isAuthenticated } = useAuth();

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const errorParam = new URLSearchParams(window.location.search).get('error');

    if (errorParam === 'google_auth_failed') {
      setError('google_auth_failed');
    }
  }, [searchParams]);

  const handleGoogleLogin = () => {
    window.location.href = `${import.meta.env.VITE_BASE_URL}/auth/google`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
    } catch (err: any) {
      if (err.message === 'Invalid credentials') {
        toast.error('Invalid email or password.', {
          position: 'bottom-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'dark',
        });
      } else {
        toast.error(err.message || 'An error occurred. Please try again.', {
          position: 'bottom-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'dark',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    setError('');
    // Clear the search parameters from the URL
    const url = new URL(window.location.href);
    url.search = '';
    window.history.replaceState({}, '', url.toString());
  };

  // If user is already authenticated, redirect to home
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Error states that should replace the login form
  const shouldShowErrorView = error === 'google_auth_failed';

  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
      <BetaMessage />
      <ToastContainer />
      <div className="flex flex-col items-center justify-center w-full sm:gap-10 md:w-auto p-4 md:p-0">
        {shouldShowErrorView ? (
          <GoogleAuthErr error={error} onRetry={handleRetry} isOverlay={true} />
        ) : (
          <>
            <IntegritasBtn />

            {/* Login Form */}
            <div className="p-4 md:p-10 bg-gradient-to-t from-black to-[#131313] w-full md:min-w-[425px] lg:min-w-[525px] rounded-lg border border-[#1B1B1B] shadow-lg relative">
              <div className="flex flex-col gap-2 text-center sm:text-left mb-4">
                <h2 className="font-normal text-lg md:text-xl text-center">
                  Welcome to Integritas+
                </h2>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <label
                      className="flex items-center gap-2 text-sm leading-none text-foreground"
                      htmlFor="email"
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      placeholder="Email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="border-[#1B1B1B] flex h-9 w-full rounded border bg-transparent px-3 py-1 text-sm shadow-xs"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="grid gap-2">
                    <label
                      className="flex items-center gap-2 text-sm leading-none text-foreground"
                      htmlFor="password"
                    >
                      Password
                    </label>
                    <input
                      id="password"
                      type="password"
                      placeholder="Password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="flex h-9 w-full rounded border border-[#1B1B1B] bg-transparent px-3 py-1 text-sm shadow-xs"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="flex flex-row justify-end gap-4 pb-4">
                  <a
                    href={`${INTEGRITAS_BASE_URL}/forgot-password`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-minima-orange-2023 text-sm cursor-pointer text-foreground font-normal"
                  >
                    Forgot password?
                  </a>
                </div>

                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center gap-2 rounded text-sm font-medium bg-minima-orange-2023 text-black shadow-xs hover:bg-minima-orange-light h-9 px-4 py-2 w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Logging in...' : 'Log in'}
                  </button>
                </div>
              </form>

              <div className="relative my-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#1B1B1B]"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-black px-2 text-foreground">or</span>
                </div>
              </div>

              <button
                className="border border-[#1B1B1B] text-sm text-minima-orange-2023 shadow-xs hover:bg-minima-orange-2023 hover:border-minima-orange-2023 hover:text-black h-9 px-4 py-2 w-full flex items-center justify-center gap-2 cursor-pointer rounded disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
                onClick={handleGoogleLogin}
              >
                <img
                  alt="Google"
                  className="w-4 h-4"
                  src="https://www.svgrepo.com/show/475656/google-color.svg"
                />
                Sign in with Google
              </button>

              {/* <div className="mt-4 text-center">
                <button className="text-sm text-muted-foreground cursor-pointer" type="button">
                  No account?{' '}
                  <a
                    href={`${INTEGRITAS_BASE_URL}/register`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-minima-orange font-semibold hover:text-minima-orange-light"
                  >
                    Create one
                  </a>
                </button>
              </div> */}
            </div>

            {/* Show regular error messages for form validation errors */}
            {error && !shouldShowErrorView && (
              <div className="mb-4 p-4 text-sm rounded border border-red-200 w-full text-center">
                <p className="text-red-600">{error}</p>
                {error.includes('sign up') && (
                  <a
                    href={`${INTEGRITAS_BASE_URL}/register`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-block text-minima-orange hover:text-minima-orange-light font-medium"
                  >
                    Sign up at Integritas →
                  </a>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
